import React, { useMemo, useState } from 'react';
import { Card, Button, Dropdown, Modal, Spinner, ProgressBar } from 'react-bootstrap';
import treeService from 'services/treeService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import useAdvanceTable from 'hooks/useAdvanceTable';

const columnHelper = createColumnHelper();

const ObjectivesTable = ({ rows, onEdit, onDelete }) => {
  const [urgencyFilter, setUrgencyFilter] = useState('Todas');
  const [mediumFilter, setMediumFilter] = useState('Todos');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerData, setViewerData] = useState([]);
  const [viewerObjective, setViewerObjective] = useState(null);
  const [viewerMode, setViewerMode] = useState('bars'); // 'bars' | 'table'
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const openViewer = async (objective) => {
    try {
      setViewerObjective(objective);
      setViewerOpen(true);
      setViewerLoading(true);
      const data = await treeService.getTree(objective.id);
      setViewerData(Array.isArray(data) ? data : []);
      
      // Expandir automáticamente todos los nodos que tienen campañas
      const keysToExpand = new Set(['0']);
      const findExpandableNodes = (nodes, pathPrefix = '') => {
        nodes.forEach((node, idx) => {
          const key = pathPrefix ? `${pathPrefix}.${idx}` : String(idx);
          const hasChildren = Array.isArray(node.children) && node.children.length > 0;
          const hasCampaigns = Array.isArray(node.campaigns) && node.campaigns.length > 0;
          
          if (hasChildren || hasCampaigns) {
            keysToExpand.add(key);
          }
          
          if (hasChildren) {
            findExpandableNodes(node.children, key);
          }
        });
      };
      
      findExpandableNodes(Array.isArray(data) ? data : []);
      setExpandedKeys(keysToExpand);
    } catch (e) {
      if (window?.toastr) window.toastr.error('No se pudo cargar el árbol');
    } finally {
      setViewerLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    let data = rows || [];
    if (urgencyFilter !== 'Todas') {
      data = data.filter(r => (r.urgency || '').toLowerCase() === urgencyFilter.toLowerCase());
    }
    if (mediumFilter !== 'Todos') {
      data = data.filter(r => (r.medium || '').toLowerCase() === mediumFilter.toLowerCase());
    }
    return data;
  }, [rows, urgencyFilter, mediumFilter]);
  const columns = useMemo(() => [
    columnHelper.accessor(row => row.clientName || row.client || '', { id: 'clientName', header: 'Cliente' }),
    columnHelper.accessor(row => row.medium || row.accountType || '', { id: 'medium', header: 'Medio', cell: info => <span className="badge bg-200 text-dark">{info.getValue()}</span> }),
    columnHelper.accessor(row => row.accountName || row.account || '', { id: 'accountName', header: 'Cuenta' }),
    columnHelper.accessor('period', { header: 'Período', cell: info => {
      const row = info.row.original;
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
          // Si es una fecha ISO string UTC, convertir correctamente a fecha local
          if (dateStr.includes('T')) {
            const date = new Date(dateStr);
            // Usar UTC para evitar problemas de zona horaria
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${day}/${month}/${year}`;
          }
          // Si ya está en formato YYYY-MM-DD, convertir también
          if (dateStr.includes('-') && dateStr.length === 10) {
            const [year, month, day] = dateStr.split('-');
            return `${day}/${month}/${year}`;
          }
          return dateStr;
        } catch (e) {
          return dateStr;
        }
      };
      return `${formatDate(row.startDate)} - ${formatDate(row.endDate)}`;
    }}),
    columnHelper.accessor('objective', { header: 'Objetivo', cell: info => {
      const row = info.row.original;
      return `${row.nominalValue?.toLocaleString()} ${row.unit}`;
    }}),
    columnHelper.accessor('urgency', { header: 'Urgencia', cell: info => {
      const v = info.getValue();
      const map = { Alta: 'danger', Media: 'warning', Baja: 'success' };
      return <span className={`badge bg-${map[v] || 'secondary'}`}>{v}</span>;
    }}),
    columnHelper.accessor('owner', { header: 'Responsable' }),
    columnHelper.display({
      id: 'actions',
      header: 'Acciones',
      cell: info => {
        const row = info.row.original;
        return (
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm" onClick={() => onEdit && onEdit(row)} title="Editar">
              <FontAwesomeIcon icon="pencil-alt" />
            </Button>
            <Button variant="outline-info" size="sm"
              onClick={() => {
                // Cambiar a tab Asignación y seleccionar objetivo
                const nav = document.querySelector('[data-rr-ui-event-key="allocation"]');
                if (nav) nav.click();
                const event = new CustomEvent('objective:allocation-open', { detail: row });
                window.dispatchEvent(event);
              }}
              title="Asignaciones"
            >
              <FontAwesomeIcon icon="project-diagram" />
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => openViewer(row)} title="Ver árbol (Real vs Objetivo)">
              <FontAwesomeIcon icon="sitemap" />
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => onDelete && onDelete(row)} title="Eliminar">
              <FontAwesomeIcon icon="trash" />
            </Button>
          </div>
        );
      }
    })
  ], [onEdit, onDelete]);

  const table = useAdvanceTable({ data: filteredRows, columns, sortable: true, pagination: true, perPage: 10 });

  return (
    <Card>
      <AdvanceTableProvider {...table}>
        <div className="d-flex justify-content-between align-items-center px-3 pt-3 flex-wrap gap-2">
          <h6 className="mb-0">Objetivos de Clientes</h6>
          <div className="d-flex align-items-center gap-2">
            <AdvanceTableSearchBox table={table} placeholder="Buscar por cliente, cuenta o responsable..." />
            <Dropdown>
              <Dropdown.Toggle size="sm" variant="light">{urgencyFilter === 'Todas' ? 'Todas las urgencias' : urgencyFilter}</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setUrgencyFilter('Todas')}>Todas las urgencias</Dropdown.Item>
                <Dropdown.Item onClick={() => setUrgencyFilter('Alta')}>Alta</Dropdown.Item>
                <Dropdown.Item onClick={() => setUrgencyFilter('Media')}>Media</Dropdown.Item>
                <Dropdown.Item onClick={() => setUrgencyFilter('Baja')}>Baja</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown>
              <Dropdown.Toggle size="sm" variant="light">{mediumFilter === 'Todos' ? 'Todos los medios' : mediumFilter}</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setMediumFilter('Todos')}>Todos los medios</Dropdown.Item>
                <Dropdown.Divider />
                {Array.from(new Set((rows || []).map(r => r.medium).filter(Boolean))).map(m => (
                  <Dropdown.Item key={m} onClick={() => setMediumFilter(m)}>{m}</Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            {(urgencyFilter !== 'Todas' || mediumFilter !== 'Todos') && (
              <Button size="sm" variant="outline-secondary" onClick={() => { setUrgencyFilter('Todas'); setMediumFilter('Todos'); }}>Limpiar</Button>
            )}
          </div>
        </div>
        <AdvanceTable table={table} />
        <div className="d-flex flex-between-center p-3">
          <AdvanceTablePagination table={table} />
        </div>
      </AdvanceTableProvider>
      <Modal show={viewerOpen} onHide={() => setViewerOpen(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Árbol del objetivo · {viewerObjective ? `${viewerObjective.clientName} - ${viewerObjective.unit || viewerObjective.name} ${Number(viewerObjective.nominalValue || 0).toLocaleString()}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '72vh', overflowY: 'auto' }}>
          {viewerLoading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" /> Cargando árbol...
            </div>
          ) : viewerData.length === 0 ? (
            <div className="text-muted">Sin datos de asignación.</div>
          ) : (
            <div className="px-1" style={{ fontSize: 13 }}>
              <div className="d-flex justify-content-end mb-2 gap-2">
                <div className="btn-group btn-group-sm" role="group">
                  <Button variant={viewerMode === 'bars' ? 'primary' : 'light'} onClick={() => setViewerMode('bars')}>Barras</Button>
                  <Button variant={viewerMode === 'table' ? 'primary' : 'light'} onClick={() => setViewerMode('table')}>Tabla</Button>
                </div>
              </div>
              {viewerMode === 'bars' ? (
                viewerData.map((root, i) => (
                  <TreeViewerNode key={i} node={root} total={Number(viewerObjective?.nominalValue || 0)} level={0} />
                ))
              ) : (
                <TreeViewerTable
                  data={viewerData}
                  total={Number(viewerObjective?.nominalValue || 0)}
                  expandedKeys={expandedKeys}
                  onToggle={(key) => {
                    const next = new Set(expandedKeys);
                    next.has(key) ? next.delete(key) : next.add(key);
                    setExpandedKeys(next);
                  }}
                />
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Card>
  );
};

const TreeViewerNode = ({ node, total, level }) => {
  const isGroup = Array.isArray(node.children) && node.children.length > 0;
  const nodePercent = Number(node.percent || 0);
  const nodeAmount = (total * nodePercent) / 100;

  return (
    <div style={{ marginLeft: level * 18 }} className="mb-3">
      <div className="d-flex align-items-center">
        <div className="me-2" style={{ width: 14, textAlign: 'center' }}>{isGroup ? (node._open ? '▼' : '▶') : '•'}</div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-semi-bold text-truncate" title={node.name}>{node.name}</span>
            <span className="badge bg-200 text-dark">{nodePercent.toFixed(2)}%</span>
            <span className="badge bg-light text-dark">{nodeAmount.toLocaleString()}</span>
          </div>
          <div className="mt-1">
            <ProgressBar now={nodePercent} variant={nodePercent === 100 ? 'success' : 'primary'} style={{ height: 8 }} />
          </div>
        </div>
      </div>
      {isGroup ? (
        <div className="mt-2">
          {node.children.map((c, idx) => (
            <TreeViewerNode key={idx} node={c} total={nodeAmount} level={level + 1} />
          ))}
        </div>
      ) : (
        Array.isArray(node.campaigns) && node.campaigns.length > 0 && (
          <div className="ms-4 mt-2">
            {node.campaigns.map((c, idx) => {
              return (
                <div key={idx} className="d-flex align-items-center gap-2 small text-muted">
                  <span className="text-truncate" style={{ maxWidth: 520 }}>{c.campaignName || c.name || `Campaña ${idx + 1}`}</span>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

const TreeViewerTable = ({ data, total, expandedKeys, onToggle }) => {
  const rows = [];
  const walk = (node, level, pathKey, parentTotal) => {
    const key = pathKey;
    const p = Number(node.percent || 0);
    const amount = (parentTotal * p) / 100;
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const hasCampaigns = Array.isArray(node.campaigns) && node.campaigns.length > 0;
    const isGroup = hasChildren || hasCampaigns; // Es grupo si tiene hijos O campañas
    
    rows.push({ key, level, name: node.name, percent: p, amount, isGroup });
    
    if (expandedKeys.has(key)) {
      // Agregar hijos (subobjetivos)
      if (Array.isArray(node.children)) {
        node.children.forEach((c, idx) => walk(c, level + 1, `${key}.${idx}`, amount));
      }
      
      // Agregar campañas como filas separadas
      if (Array.isArray(node.campaigns) && node.campaigns.length > 0) {
        node.campaigns.forEach((c, idx) => {
          const campaignKey = `${key}.campaign.${idx}`;
          const campaignName = c.campaignName || c.name || `Campaña ${idx + 1}`;
          rows.push({ 
            key: campaignKey, 
            level: level + 1, 
            name: campaignName, 
            percent: 0, 
            amount: 0, 
            isGroup: false,
            isCampaign: true 
          });
        });
      }
    }
  };
  data.forEach((root, i) => walk(root, 0, String(i), total));

  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th style={{ width: '60%' }}>Nodo</th>
            <th className="text-end" style={{ width: 100 }}>%</th>
            <th className="text-end" style={{ width: 140 }}>Monto</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>
                <div className="d-flex align-items-center" style={{ marginLeft: r.level * 16 }}>
                  {r.isGroup ? (
                    <button className="btn btn-link p-0 me-2" onClick={() => onToggle(r.key)} aria-label="toggle">
                      {expandedKeys.has(r.key) ? '▼' : '▶'}
                    </button>
                  ) : (
                    <span className="me-2" style={{ width: 16, display: 'inline-block', textAlign: 'center' }}>
                      {r.isCampaign ? '📋' : '•'}
                    </span>
                  )}
                  <span className={`text-truncate ${r.isCampaign ? 'text-muted fst-italic' : ''}`} title={r.name}>
                    {r.name}
                  </span>
                </div>
              </td>
              <td className="text-end">
                {!r.isCampaign && <span className="badge bg-200 text-dark">{r.percent.toFixed(2)}%</span>}
              </td>
              <td className="text-end">
                {!r.isCampaign && <span className="badge bg-light text-dark">{r.amount.toLocaleString()}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ObjectivesTable;


